<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { requiredIf } from '@vuelidate/validators'
import { computed } from 'vue'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
}>()

// Reset main applicant risk value (only if editable)
if (!props.disable && umsokn.value && umsokn.value.moneyLaunderingRisk === false) {
  ;(umsokn.value as any).moneyLaunderingRisk = null
}

// Validation rules (dynamic for co-deptors)
const rules = computed(() => ({
  umsokn: {
    // Main applicant
    moneyLaunderingRisk: {
      moneyLaunderingRequired: (v: any) => v === true || v === false
    },
    moneyLaunderingRiskWhy: {
      required: requiredIf(() => umsokn.value?.moneyLaunderingRisk === true)
    },

    // Co-deptors (dynamic array)
    coDeptors: Array.isArray(umsokn.value?.coDeptors)
      ? umsokn.value.coDeptors.map((cd: any) => ({
          moneyLaunderingRiskCoDeptor: {
            moneyLaunderingRequired: (v: any) => v === true || v === false
          },
          moneyLaunderingRiskWhy: {
            required: requiredIf(() => cd?.moneyLaunderingRiskCoDeptor === true)
          }
        }))
      : []
  }
}))

const v$ = useVuelidate(
  rules,
  { umsokn }
)

defineExpose({
  validate() {
    v$.value.$touch()

    // touch subitems
    if (Array.isArray(umsokn.value?.coDeptors)) {
      umsokn.value.coDeptors.forEach((_:number, i:number) => {
        v$.value.umsokn.coDeptors?.[i]?.$touch()
      })
    }

    return !v$.value.$invalid
  }
})

function breakLine(message: string): string[] {
  const custom = message.split('-')
  for (let i = 1; i < custom.length; i++) {
    custom[i] = '-' + custom[i]
  }
  return custom.slice(1)
}
</script>

<template>
  <div v-if="umsokn" class="col-sm-12">

    <!-- MAIN APPLICANT -->
    <v-row>
      <v-col>
        <h2 class="forms__header m-t-20">
          {{ t('umsokn_PEP_header') }}
        </h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-radio-group
          v-model="umsokn.moneyLaunderingRisk"
          :readonly="props.disable"
          :error="v$.umsokn.moneyLaunderingRisk.$error"
          :error-messages="
            v$.umsokn.moneyLaunderingRisk.$error ? [t('umsokn_PEP_error')] : []
          "
        >
          <v-radio :label="t('umsokn_PEP_no')" :value="false" />
          <v-radio :label="t('umsokn_PEP_yes')" :value="true" />
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row v-if="umsokn.moneyLaunderingRisk">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.moneyLaunderingRiskWhy"
          :label="t('Umsokn_PEP_why')"
          :readonly="props.disable"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
          :error="v$.umsokn.moneyLaunderingRiskWhy.$error"
          :error-messages="
            v$.umsokn.moneyLaunderingRiskWhy.$error ? [t('umsokn_PEP_Why_error')] : []
          "
        />
      </v-col>
    </v-row>

    <!-- CO-DEPTORS -->
    <div
      v-for="(cd, i) in umsokn.coDeptors"
      :key="'pep_cd_' + i"
      class="mt-10"
    >
      <h3 class="forms__header">
        {{ t('umsokn_PEP_CoDebtor', { name: cd.name || '' }) }}
      </h3>

      <v-radio-group
        v-model="cd.moneyLaunderingRiskCoDeptor"
        :readonly="props.disable"
        :error="v$.umsokn.coDeptors[i].moneyLaunderingRiskCoDeptor.$error"
        :error-messages="
          v$.umsokn.coDeptors[i].moneyLaunderingRiskCoDeptor.$error
            ? [t('umsokn_PEP_error')]
            : []
        "
      >
        <v-radio :label="t('umsokn_PEP_no')" :value="false" />
        <v-radio :label="t('umsokn_PEP_yes')" :value="true" />
      </v-radio-group>

      <v-row v-if="cd.moneyLaunderingRiskCoDeptor">
        <v-col cols="12" md="6">
          <v-text-field
            v-model="cd.moneyLaunderingRiskWhy"
            :label="t('umsokn_PEP_CoDebtor')"
            :readonly="props.disable"
            variant="outlined"
            density="comfortable"
            hide-details="auto"
            :error="v$.umsokn.coDeptors[i].moneyLaunderingRiskWhy.$error"
            :error-messages="
              v$.umsokn.coDeptors[i].moneyLaunderingRiskWhy.$error
                ? [t('umsokn_PEP_Why_error')]
                : []
            "
          />
        </v-col>
      </v-row>
    </div>

    <!-- Disclaimer -->
    <v-row v-if="!props.disable">
      <v-col>
        <div class="large-label m-t-20">
          {{ t('umsokn_PEP_clarification') }}
        </div>
        <div class="mt-4">{{ t('umsokn_PEP_clarification_1') }}</div>
        <div class="mt-4">
          <strong>{{ t('umsokn_PEP_clarification_2') }}</strong>
        </div>
        <div class="small-label mt-4">
          {{ t('umsokn_PEP_clarification_3') }}
        </div>
        <div class="breakline mt-4">
          <span
            class="broken-line"
            v-for="(line, idx) in breakLine(t('umsokn_PEP_clarification_4'))"
            :key="idx"
          >
            {{ line }} <br />
          </span>
        </div>
        <div class="small-label mt-4">
          {{ t('umsokn_PEP_clarification_5') }}
        </div>
        <div class="mt-4">{{ t('umsokn_PEP_clarification_6') }}</div>
        <div class="small-label mt-4">
          {{ t('umsokn_PEP_clarification_7') }}
        </div>
        <div class="breakline mt-4">
          <span
            class="broken-line"
            v-for="(line, idx) in breakLine(t('umsokn_PEP_clarification_8'))"
            :key="idx"
          >
            {{ line }} <br />
          </span>
        </div>
      </v-col>
    </v-row>

  </div>
</template>

<style scoped lang="scss">
.label_question {
  color: #194458;
  font-size: 18px;
  line-height: 34px;
}
.large-label {
  font-size: 24px;
  font-weight: bold;
}
.small-label,
.large-label {
  font-weight: bold;
}
.breakline .broken-line {
  padding: 0;
}
</style>
